import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { SelectInput } from '../inputs';
import { apiGet, apiPost, apiDelete, showErrorToast } from '../../services/apiClient';
import { API_ENDPOINTS } from '../../config/apiEndpoints';
import { UserPlatformSubRole, UserPlatformRole } from '../../models/user';
import { AuthContext } from '../../context/authContext';
import { useTranslation } from 'react-i18next';
import { toast } from '../../utils/toast';
import TextInput from '../inputs/textInput';
import useConfirm from '../../hooks/ui/useConfirm';

export interface Employee {
  id: number;
  name: string;
  email: string;
  subRole: UserPlatformSubRole;
  role: number;
  defaultLanguage: number;
  createdAt: string;
  lockedAt: string | null;
}

interface TeamManagementProps {
  tenantId: number;
}

const TeamManagement: React.FC<TeamManagementProps> = ({ tenantId }) => {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);
  const isAdmin = authContext?.user?.role === UserPlatformRole.ADMIN;
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { confirm, setConfirmIsOpen, setConfirmTitle, setConfirmDescription, setConfirmVariant, setConfirmText, ConfirmModal } = useConfirm();

  const SUB_ROLE_OPTIONS = [
    { key: t('Director'), value: String(UserPlatformSubRole.DIRECTOR) },
    { key: t('Truck Manager'), value: String(UserPlatformSubRole.TRUCK_MANAGER) },
    { key: t('Truck Driver'), value: String(UserPlatformSubRole.TRUCK_DRIVER) },
    { key: t('Reviewer'), value: String(UserPlatformSubRole.REVIEWER) },
    { key: t('None'), value: String(UserPlatformSubRole.NONE) },
  ];

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchEmployees = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = API_ENDPOINTS.tenant.employees(tenantId);
      const response = await apiGet(url, {offset: page * rowsPerPage, limit: Math.min(rowsPerPage, 300), search: debouncedSearch});
      const data = await response.json();
      
      setEmployees(data.result || []);
      setTotalCount(data.count || 0);
    } catch (err) {
      setError(t('Failed to load team members'));
      showErrorToast(err, t('Failed to load team members'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tenantId) {
      fetchEmployees();
    }
  }, [tenantId, page, rowsPerPage, debouncedSearch]);

  const handleRoleChange = async (userId: number, newRole: UserPlatformSubRole) => {
    try {
      const url = API_ENDPOINTS.tenant.updateEmployeeRole(tenantId, userId);
      
      await apiPost(url, { subRole: newRole });
      toast.success(t('Role updated successfully'));
      fetchEmployees();
    } catch (err) {
      showErrorToast(err, t('Failed to update role'));
    }
  };

  const handleToggleLock = async (employee: Employee) => {
    const isActivating = !!employee.lockedAt;

    setConfirmTitle(isActivating ? t('Activate user') : t('Deactivate user'));
    setConfirmVariant('warning');
    setConfirmDescription(
      isActivating
        ? <>{t('Are you sure you want to activate')} <strong>{employee.name}</strong>?</>
        : <>{t('Are you sure you want to deactivate')} <strong>{employee.name}</strong>?</>
    );
    setConfirmText(isActivating ? 'Activate' : 'Deactivate');
    const ok = await confirm();
    setConfirmIsOpen(false);
    if (!ok) return;

    try {
      const url = API_ENDPOINTS.admin.lockUser(employee.id);
      await apiPost(url, { lock: !isActivating });
      toast.success(isActivating ? t('User activated successfully') : t('User deactivated successfully'));
      fetchEmployees();
    } catch (err) {
      showErrorToast(err, isActivating ? t('Failed to activate user') : t('Failed to deactivate user'));
    }
  };

  const handleRemoveEmployee = async (userId: number, userName: string) => {
    if (!window.confirm(t('Are you sure you want to remove {{name}} from the team?', { name: userName }))) {
      return;
    }

    try {
      const url = API_ENDPOINTS.tenant.removeEmployee(tenantId, userId);
      
      await apiDelete(url);
      toast.success(t('Employee removed successfully'));
      fetchEmployees();
    } catch (err) {
      showErrorToast(err, t('Failed to remove employee'));
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
          {t('Team Management')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('Manage your team members, their roles, and permissions.')}
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextInput
          fullWidth
          placeholder={t('Search by name or email')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('Name')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('Email')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('Role')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('Joined')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('Activated')}</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 100 }}>{t('Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('No team members found')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  employees.map((employee) => (
                    <TableRow key={employee.id}>
                      <TableCell>{employee.name}</TableCell>
                      <TableCell>{employee.email}</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        <SelectInput
                          fullWidth
                          value={String(employee.subRole)}
                          onChange={(e) => handleRoleChange(employee.id, Number(e.target.value) as UserPlatformSubRole)}
                          options={SUB_ROLE_OPTIONS}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {employee.createdAt ? new Date(employee.createdAt).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell>
                        {!employee.lockedAt ? (
                          <Tooltip title={isAdmin ? t('Click to deactivate user') : t('Admin only')}>
                            <span>
                              <IconButton
                                onClick={() => handleToggleLock(employee)}
                                size="small"
                                disableRipple
                                disabled={!isAdmin}
                              >
                                <CheckCircleIcon color={isAdmin ? 'success' : 'disabled'} fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title={isAdmin ? t('Click to activate user') : t('Admin only')}>
                            <span>
                              <Button
                                variant="outlined"
                                color="warning"
                                size="small"
                                onClick={() => handleToggleLock(employee)}
                                disabled={!isAdmin}
                                sx={{ textTransform: 'none' }}
                              >
                                {t('Pending')}
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={t('Remove employee')}>
                          <IconButton
                            onClick={() => handleRemoveEmployee(employee.id, employee.name)}
                            size="small"
                            disableRipple
                            sx={{
                              color: 'text.grey',
                              '&:hover': {
                                color: 'error.main',
                                backgroundColor: 'transparent',
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </>
      )}
      {ConfirmModal}
    </Box>
  );
};

export default TeamManagement;
